import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './NonConformances.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SEVERITY_CONFIG = {
  minor:    { label: 'Minor',    colour: '#f5b50b', bg: 'rgba(245,181,11,0.12)',  border: 'rgba(245,181,11,0.3)'  },
  major:    { label: 'Major',    colour: '#FF9800', bg: 'rgba(255,152,0,0.12)',   border: 'rgba(255,152,0,0.3)'   },
  critical: { label: 'Critical', colour: '#ef5350', bg: 'rgba(239,83,80,0.12)',   border: 'rgba(239,83,80,0.3)'   },
};

const STATUS_CONFIG = {
  'open':        { label: 'Open',        cls: 'status-open'        },
  'in progress': { label: 'In Progress', cls: 'status-inprogress'  },
  'closed':      { label: 'Closed',      cls: 'status-closed'      },
  'overdue':     { label: 'Overdue',     cls: 'status-overdue'     },
};

const SOURCE_OPTIONS = [
  'Internal Audit',
  'External Audit',
  'Customer Complaint',
  'Supplier Issue',
  'Site Observation',
  'Near Miss',
  'Management Review',
  'Other',
];

const EMPTY_FORM = {
  title: '', description: '', source: 'Internal Audit', severity: 'minor',
  assigned_to: '', corrective_action_due: '', notes: '', recurrence: false,
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysSince(str) {
  if (!str) return null;
  return Math.floor((new Date() - new Date(str)) / 86400000);
}

function daysUntil(str) {
  if (!str) return null;
  return Math.ceil((new Date(str) - new Date()) / 86400000);
}

export default function NonConformances({ user }) {
  const [ncrs, setNcrs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'add' | 'detail'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // For closing an NCR
  const [closeMode, setCloseMode] = useState(false);
  const [closeForm, setCloseForm] = useState({ root_cause: '', corrective_action: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [ncrRes, userRes] = await Promise.all([
        axios.get(`${API}/non-conformances`),
        axios.get(`${API}/users/all`),
      ]);
      setNcrs(ncrRes.data);
      setUsers(userRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const counts = useMemo(() => ({
    open:       ncrs.filter(n => n.status === 'open').length,
    inProgress: ncrs.filter(n => n.status === 'in progress').length,
    overdue:    ncrs.filter(n => n.status === 'overdue' || (n.status !== 'closed' && n.corrective_action_due && daysUntil(n.corrective_action_due) < 0)).length,
    closed:     ncrs.filter(n => n.status === 'closed').length,
    total:      ncrs.length,
  }), [ncrs]);

  const filtered = useMemo(() => {
    return ncrs
      .filter(n => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'overdue') return n.status !== 'closed' && n.corrective_action_due && daysUntil(n.corrective_action_due) < 0;
        return n.status === statusFilter;
      })
      .filter(n => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (n.reference_number || '').toLowerCase().includes(q) ||
          (n.title || '').toLowerCase().includes(q) ||
          (n.source || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => new Date(b.raised_at) - new Date(a.raised_at));
  }, [ncrs, statusFilter, search]);

  const userName = (id) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : '—';
  };

  const handleRaise = async () => {
    if (!form.title) { alert('Title is required.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/non-conformances`, {
        ...form,
        assigned_to: form.assigned_to || null,
        corrective_action_due: form.corrective_action_due || null,
        recurrence: form.recurrence === true || form.recurrence === 'true',
        status: 'open',
        raised_by: user.id,
        created_by: user.id,
      });
      setForm(EMPTY_FORM);
      setView('list');
      fetchAll();
    } catch (err) { alert('Error raising NCR: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await axios.patch(`${API}/non-conformances/${id}`, { ...updates, updated_by: user.id });
      fetchAll();
      // Refresh selected
      const res = await axios.get(`${API}/non-conformances`);
      const updated = res.data.find(n => n.id === id);
      if (updated) setSelected(updated);
    } catch (err) { alert('Error updating: ' + err.message); }
  };

  const handleClose = async () => {
    if (!closeForm.root_cause) { alert('Root cause is required to close an NCR.'); return; }
    if (!closeForm.corrective_action) { alert('Corrective action is required to close an NCR.'); return; }
    setSaving(true);
    try {
      // First save root cause and corrective action
      await axios.patch(`${API}/non-conformances/${selected.id}`, {
        root_cause: closeForm.root_cause,
        corrective_action: closeForm.corrective_action,
        updated_by: user.id,
      });
      // Then close
      await axios.patch(`${API}/non-conformances/${selected.id}/close`, { verified_by: user.id });
      setCloseMode(false);
      setCloseForm({ root_cause: '', corrective_action: '' });
      fetchAll();
      setView('list');
    } catch (err) { alert('Error closing NCR: ' + err.message); }
    finally { setSaving(false); }
  };

  const openDetail = (ncr) => {
    setSelected(ncr);
    setCloseMode(false);
    setCloseForm({ root_cause: ncr.root_cause || '', corrective_action: ncr.corrective_action || '' });
    setView('detail');
  };

  const getEffectiveStatus = (ncr) => {
    if (ncr.status === 'closed') return 'closed';
    if (ncr.corrective_action_due && daysUntil(ncr.corrective_action_due) < 0) return 'overdue';
    return ncr.status;
  };

  if (loading) return <div className="nc-loading">Loading non-conformances...</div>;

  // ── RAISE NEW NCR ────────────────────────────────────────────
  if (view === 'add') return (
    <div className="nc-form-page">
      <button className="nc-back" onClick={() => { setView('list'); setForm(EMPTY_FORM); }}>← NON-CONFORMANCES</button>
      <div className="nc-form-header">
        <h2>RAISE NON-CONFORMANCE</h2>
        <p>A reference number will be assigned automatically</p>
      </div>
      <div className="nc-form">
        <div className="nc-field full">
          <label>TITLE *</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Brief description of the non-conformance" />
        </div>
        <div className="nc-field full">
          <label>DESCRIPTION</label>
          <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detailed description of what was observed or reported..." />
        </div>
        <div className="nc-field">
          <label>SOURCE</label>
          <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
            {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="nc-field">
          <label>SEVERITY</label>
          <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
            <option value="minor">Minor</option>
            <option value="major">Major</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div className="nc-field">
          <label>ASSIGN TO</label>
          <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
            <option value="">— Select person —</option>
            {users.filter(u => !u.is_archived).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
        <div className="nc-field">
          <label>CORRECTIVE ACTION DUE BY</label>
          <input type="date" value={form.corrective_action_due} onChange={e => setForm({ ...form, corrective_action_due: e.target.value })} />
        </div>
        <div className="nc-field">
          <label>RECURRENCE?</label>
          <select value={form.recurrence} onChange={e => setForm({ ...form, recurrence: e.target.value === 'true' })}>
            <option value="false">No — first occurrence</option>
            <option value="true">Yes — has occurred before</option>
          </select>
        </div>
        <div className="nc-field full">
          <label>NOTES</label>
          <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
        </div>
        <div className="nc-form-actions">
          <button className="nc-btn secondary" onClick={() => { setView('list'); setForm(EMPTY_FORM); }}>CANCEL</button>
          <button className={`nc-btn primary ${saving ? 'disabled' : ''}`} onClick={handleRaise} disabled={saving}>
            {saving ? 'RAISING...' : 'RAISE NCR'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const status = getEffectiveStatus(selected);
    const sev = SEVERITY_CONFIG[selected.severity] || SEVERITY_CONFIG.minor;
    const isClosed = status === 'closed';

    return (
      <div className="nc-detail-page">
        <button className="nc-back" onClick={() => { setView('list'); setSelected(null); setCloseMode(false); }}>← NON-CONFORMANCES</button>

        <div className="nc-detail-header">
          <div className="nc-detail-ref">{selected.reference_number}</div>
          <h2 className="nc-detail-title">{selected.title}</h2>
          <div className="nc-detail-meta">
            <span className={`nc-status-badge ${STATUS_CONFIG[status]?.cls || 'status-open'}`}>{STATUS_CONFIG[status]?.label || status}</span>
            <span className="nc-sev-badge" style={{ color: sev.colour, background: sev.bg, border: `1px solid ${sev.border}` }}>{sev.label}</span>
            {selected.recurrence && <span className="nc-recurrence-badge">⚠ Recurring Issue</span>}
          </div>
        </div>

        <div className="nc-detail-grid">
          {/* Left column — NCR info */}
          <div className="nc-detail-col">
            <div className="nc-section-block">
              <div className="nc-block-title">DETAILS</div>
              <div className="nc-block-row"><span>Source</span><span>{selected.source || '—'}</span></div>
              <div className="nc-block-row"><span>Raised by</span><span>{userName(selected.raised_by)}</span></div>
              <div className="nc-block-row"><span>Raised on</span><span>{formatDate(selected.raised_at)}</span></div>
              <div className="nc-block-row"><span>Assigned to</span><span>{selected.assigned_to ? userName(selected.assigned_to) : '—'}</span></div>
              <div className="nc-block-row"><span>Due by</span>
                <span className={selected.corrective_action_due && daysUntil(selected.corrective_action_due) < 0 && !isClosed ? 'nc-overdue-text' : ''}>
                  {formatDate(selected.corrective_action_due)}
                  {selected.corrective_action_due && !isClosed && daysUntil(selected.corrective_action_due) >= 0 &&
                    <span className="nc-days-badge"> {daysUntil(selected.corrective_action_due)}d remaining</span>}
                  {selected.corrective_action_due && !isClosed && daysUntil(selected.corrective_action_due) < 0 &&
                    <span className="nc-days-badge overdue"> {Math.abs(daysUntil(selected.corrective_action_due))}d overdue</span>}
                </span>
              </div>
            </div>

            {selected.description && (
              <div className="nc-section-block">
                <div className="nc-block-title">DESCRIPTION</div>
                <p className="nc-block-text">{selected.description}</p>
              </div>
            )}

            {selected.notes && (
              <div className="nc-section-block">
                <div className="nc-block-title">NOTES</div>
                <p className="nc-block-text">{selected.notes}</p>
              </div>
            )}
          </div>

          {/* Right column — investigation & close */}
          <div className="nc-detail-col">
            {/* Root cause */}
            <div className="nc-section-block">
              <div className="nc-block-title">ROOT CAUSE ANALYSIS</div>
              {isClosed ? (
                <p className="nc-block-text">{selected.root_cause || '—'}</p>
              ) : (
                <textarea
                  className="nc-inline-textarea"
                  rows={4}
                  value={closeForm.root_cause}
                  onChange={e => setCloseForm({ ...closeForm, root_cause: e.target.value })}
                  placeholder="What was the root cause of this non-conformance?"
                />
              )}
            </div>

            {/* Corrective action */}
            <div className="nc-section-block">
              <div className="nc-block-title">CORRECTIVE ACTION</div>
              {isClosed ? (
                <p className="nc-block-text">{selected.corrective_action || '—'}</p>
              ) : (
                <textarea
                  className="nc-inline-textarea"
                  rows={4}
                  value={closeForm.corrective_action}
                  onChange={e => setCloseForm({ ...closeForm, corrective_action: e.target.value })}
                  placeholder="What action has been or will be taken to prevent recurrence?"
                />
              )}
            </div>

            {/* Closed info */}
            {isClosed && (
              <div className="nc-section-block nc-closed-block">
                <div className="nc-block-title">CLOSURE</div>
                <div className="nc-block-row"><span>Verified by</span><span>{userName(selected.verified_by)}</span></div>
                <div className="nc-block-row"><span>Closed on</span><span>{formatDate(selected.verified_at)}</span></div>
                <div className="nc-block-row"><span>Open for</span><span>{daysSince(selected.raised_at) - daysSince(selected.verified_at)} days</span></div>
              </div>
            )}

            {/* Actions */}
            {!isClosed && user.role === 'admin' && (
              <div className="nc-detail-actions">
                {selected.status === 'open' && (
                  <button className="nc-btn secondary" onClick={() => handleUpdate(selected.id, { status: 'in progress' })}>
                    MARK IN PROGRESS
                  </button>
                )}
                <button
                  className="nc-btn primary"
                  onClick={handleClose}
                  disabled={saving}
                >
                  {saving ? 'CLOSING...' : 'CLOSE NCR'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────
  return (
    <div className="nc-page">

      {/* Stat cards */}
      <div className="nc-stats">
        <div className="nc-stat" onClick={() => setStatusFilter('all')} style={{ cursor: 'pointer' }}>
          <span className="nc-stat-value">{counts.total}</span>
          <span className="nc-stat-label">Total</span>
        </div>
        <div className="nc-stat open" onClick={() => setStatusFilter('open')} style={{ cursor: 'pointer' }}>
          <span className="nc-stat-value">{counts.open}</span>
          <span className="nc-stat-label">Open</span>
        </div>
        <div className="nc-stat inprogress" onClick={() => setStatusFilter('in progress')} style={{ cursor: 'pointer' }}>
          <span className="nc-stat-value">{counts.inProgress}</span>
          <span className="nc-stat-label">In Progress</span>
        </div>
        <div className="nc-stat overdue" onClick={() => setStatusFilter('overdue')} style={{ cursor: 'pointer' }}>
          <span className="nc-stat-value">{counts.overdue}</span>
          <span className="nc-stat-label">Overdue</span>
        </div>
        <div className="nc-stat closed" onClick={() => setStatusFilter('closed')} style={{ cursor: 'pointer' }}>
          <span className="nc-stat-value">{counts.closed}</span>
          <span className="nc-stat-label">Closed</span>
        </div>
      </div>

      {/* Controls */}
      <div className="nc-controls">
        <input
          className="nc-search"
          type="text"
          placeholder="Search NCRs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="nc-filter-group">
          {[
            { key: 'all', label: 'ALL' },
            { key: 'open', label: 'OPEN' },
            { key: 'in progress', label: 'IN PROGRESS' },
            { key: 'overdue', label: 'OVERDUE' },
            { key: 'closed', label: 'CLOSED' },
          ].map(f => (
            <button key={f.key} className={`nc-filter-btn ${statusFilter === f.key ? 'active' : ''}`} onClick={() => setStatusFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        {user.role === 'admin' && (
          <button className="nc-raise-btn" onClick={() => setView('add')}>+ RAISE NCR</button>
        )}
      </div>

      <div className="nc-count">{filtered.length} non-conformance{filtered.length !== 1 ? 's' : ''}</div>

      {/* NCR List */}
      {filtered.length === 0 ? (
        <div className="nc-empty">
          <div className="nc-empty-icon">✓</div>
          <p>{statusFilter === 'all' ? 'No non-conformances raised yet.' : `No ${statusFilter} non-conformances.`}</p>
          {statusFilter === 'all' && user.role === 'admin' && (
            <button className="nc-btn primary" onClick={() => setView('add')}>Raise your first NCR</button>
          )}
        </div>
      ) : (
        <div className="nc-list">
          {filtered.map(ncr => {
            const status = getEffectiveStatus(ncr);
            const sev = SEVERITY_CONFIG[ncr.severity] || SEVERITY_CONFIG.minor;
            const due = ncr.corrective_action_due ? daysUntil(ncr.corrective_action_due) : null;
            return (
              <div key={ncr.id} className="nc-card" onClick={() => openDetail(ncr)}>
                <div className="nc-card-left">
                  <div className="nc-card-ref">{ncr.reference_number}</div>
                  <div className="nc-card-title">{ncr.title}</div>
                  <div className="nc-card-meta">
                    <span className="nc-card-source">{ncr.source}</span>
                    <span>Raised {formatDate(ncr.raised_at)}</span>
                    {ncr.assigned_to && <span>→ {userName(ncr.assigned_to)}</span>}
                    {ncr.recurrence && <span className="nc-recurrence-tag">Recurring</span>}
                  </div>
                </div>
                <div className="nc-card-right">
                  <span className="nc-sev-badge" style={{ color: sev.colour, background: sev.bg, border: `1px solid ${sev.border}` }}>{sev.label}</span>
                  <span className={`nc-status-badge ${STATUS_CONFIG[status]?.cls || 'status-open'}`}>{STATUS_CONFIG[status]?.label || status}</span>
                  {due !== null && status !== 'closed' && (
                    <span className={`nc-due-chip ${due < 0 ? 'overdue' : due <= 7 ? 'urgent' : ''}`}>
                      {due < 0 ? `${Math.abs(due)}d overdue` : `${due}d left`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
